import React, { useCallback, useEffect, useState } from 'react';
import { Button, Dialog } from '../../../components/ui';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import BusinessFunctionCreateForm from './BusinessFunctionCreateForm.jsx';
import useBusinessFunctionCreateForm from '../hooks/useBusinessFunctionCreateForm';

const SUBTITLE =
  'Set up a business function for your organisation. Fill in the details and add at least one contact person.';

export default function CreateBusinessFunctionDialog({
  open,
  organisationId,
  organisationName,
  onClose,
  onCreated,
}) {
  const { showSnackBar } = useSnackBar();
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);
  const selectedOrganisationId =
    selectedOrganisation?.organisation_id || selectedOrganisation?.id || '';

  useEffect(() => {
    if (!open) return;
    setSelectedOrganisation(
      organisationId
        ? {
            id: organisationId,
            organisation_id: organisationId,
            name: organisationName || '',
            label: organisationName || '',
          }
        : null
    );
  }, [open, organisationId, organisationName]);

  const formState = useBusinessFunctionCreateForm({
    open,
    organisationId: selectedOrganisationId,
    onCreated,
    onClose,
  });

  const {
    form,
    contacts,
    detailErrors,
    contactErrors,
    isSubmitting,
    submitError,
    updateField,
    updateContact,
    addContact,
    removeContact,
    submit,
  } = formState;

  const handleSave = useCallback(async () => {
    const result = await submit();
    if (result.success) {
      showSnackBar('Business function created successfully.', 'success');
    } else if (result.message) {
      showSnackBar(result.message, 'error');
    }
  }, [showSnackBar, submit]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create Business Function"
      subtitle={SUBTITLE}
      maxWidth="lg"
      contentSx={{
        px: { xs: 2, sm: 3 },
        py: 2.5,
        maxHeight: 'min(78vh, 760px)',
        overflowY: 'auto',
      }}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" loading={isSubmitting} onClick={handleSave}>
            Create business function
          </Button>
        </>
      }
    >
      <BusinessFunctionCreateForm
        form={form}
        contacts={contacts}
        detailErrors={detailErrors}
        contactErrors={contactErrors}
        selectedOrganisation={selectedOrganisation}
        onOrganisationChange={setSelectedOrganisation}
        onFieldChange={updateField}
        onContactChange={updateContact}
        onAddContact={addContact}
        onRemoveContact={removeContact}
        submitError={submitError}
      />
    </Dialog>
  );
}
